import { useGetUserEntitlements } from "@/api/generated";
import type { Entitlement, EntitlementStatus } from "@/api/generated/models";
import {
  H3,
  Header,
  Paragraph,
  Section,
  Separator,
  Skeleton,
  Text,
  XStack,
  YStack,
  useMedia,
} from "@/components";

/**
 * Debug view of the caller's reduced entitlement ledger — what grants they hold and whether each
 * currently grants access. Shown only in non-production; the gate lives in ../dialog.tsx.
 */
export default function EntitlementsSettingsScreen() {
  const { lg } = useMedia();
  const { data, isLoading } = useGetUserEntitlements();

  const entitlements = data?.entitlements ?? [];

  return (
    <Section margin="$screenPadding" flex={1} $lg={{ flex: 1, gap: "$250" }}>
      {lg && (
        <Header gap="$250">
          <H3>Entitlements</H3>
          <Separator borderColor="$fillNeutral" />
        </Header>
      )}
      {isLoading ? (
        <YStack gap="$150">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton height="$800" key={i} />
          ))}
        </YStack>
      ) : entitlements.length === 0 ? (
        <Paragraph color="$textSecondary">
          No entitlements — this user has no grants on their ledger.
        </Paragraph>
      ) : (
        <YStack gap="$150">
          {entitlements.map((entitlement) => (
            <EntitlementCard
              key={entitlement.grant_id}
              entitlement={entitlement}
            />
          ))}
        </YStack>
      )}
    </Section>
  );
}

function EntitlementCard({ entitlement }: { entitlement: Entitlement }) {
  const {
    grant_type,
    status,
    features,
    remaining_usages,
    valid_from,
    valid_until,
    source_type,
  } = entitlement;

  return (
    <YStack
      gap="$100"
      borderWidth="$25"
      borderColor="$fillNeutral"
      borderRadius="$150"
      padding="$200"
    >
      <XStack justifyContent="space-between" alignItems="center" gap="$100">
        <Text size="$sm" fontWeight="bold">
          {grant_type}
        </Text>
        <StatusPill status={status} />
      </XStack>
      <Field label="Features" value={features.join(", ") || "—"} />
      <Field
        label="Usages left"
        value={
          remaining_usages == null ? "Unlimited" : String(remaining_usages)
        }
      />
      <Field
        label="Valid"
        value={`${formatDate(valid_from)} → ${formatDate(valid_until)}`}
      />
      <Field label="Source" value={source_type} />
    </YStack>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <XStack gap="$100" flexWrap="wrap">
      <Text size="$xs" color="$textTertiary" width="$1200">
        {label}
      </Text>
      <Text size="$xs" color="$textSecondary" flex={1}>
        {value}
      </Text>
    </XStack>
  );
}

function StatusPill({ status }: { status: EntitlementStatus }) {
  const active = status === "ACTIVE";
  return (
    <Text
      size="$xs"
      color={active ? "$textPrimary" : "$textSecondary"}
      backgroundColor={active ? "$fillBadgeCurai" : "$fillNeutral"}
      paddingHorizontal="$100"
      paddingVertical="$25"
      borderRadius="$100"
    >
      {status}
    </Text>
  );
}

// Entitlement validity timestamps are ISO strings (or null == open-ended).
function formatDate(iso: string | null): string {
  if (!iso) return "∞";
  return new Date(iso).toLocaleDateString();
}
